import {
  createQueryStore,
  createMutationStore,
  useQueryClient,
} from '@ferreri/minato-react-utils';
import {
  deleteEstablishmentImages,
  getEstablishmentImages,
  postEstablishmentImages,
} from './establishment-images.client';

type FormData = {
  company_id: string;
  establishment_id: string;
  files: {
    to_create: [{ file: File[] }];
    to_delete: number[];
    to_update: { id: number; file: File }[];
  };
};

export const useSaveEstablishmentImages = () => {
  const {
    mutateAsync: saveEstablishmentImages,
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
  } = useCreateCompaniesEstablishmentsImages();
  const {
    mutateAsync: deleteEstablishmentImages,
    isSuccess: isDeleteSuccess,
    isLoading: isDeleteLoading,
  } = useDeleteEstablishmentImages();

  const isLoading = isCreateLoading || isDeleteLoading;
  const isSuccess = isCreateSuccess || isDeleteSuccess;

  const mutate = async ({ files, establishment_id }: FormData) => {
    const mutations = [
      ...[...(files?.to_create || []), ...(files?.to_update || [])].map(
        ({ file }) => saveEstablishmentImages({ file, establishment_id }),
      ),
      ...(files?.to_delete || []).map((establishmentImageId) =>
        deleteEstablishmentImages(establishmentImageId),
      ),
    ].reduce((acc, mutation) => acc.concat(mutation), []);
    await Promise.all(mutations);
  };

  return {
    mutate,
    isLoading,
    isSuccess,
  };
};

export const useEstablishmentImage = (establishmentId, config = null) => {
  return createQueryStore({
    queryKey: ['establishment', establishmentId, 'image'],
    queryFn: () => getEstablishmentImages(establishmentId),
    refetchOnWindowFocus: false,
    ...config,
  });
};

const useCreateCompaniesEstablishmentsImages = (config = null) => {
  return createMutationStore({
    mutationFn: postEstablishmentImages,
    ...getCombinedConfig(config),
  });
};

const useDeleteEstablishmentImages = (config = null) => {
  return createMutationStore({
    mutationFn: (establishmentImageId) =>
      deleteEstablishmentImages(establishmentImageId),
    ...getCombinedConfig(config),
  });
};

const getCombinedConfig = (config) => {
  const queryClient = useQueryClient();

  return {
    ...config,
    onSuccess: (data) => {
      config?.onSuccess?.(data);
      queryClient.invalidateQueries();
    },
  };
};
